import {
  bufferToUint8Array,
  convertJsonToUint8Array,
  createSharedBuffer,
  parseJSONUint8Array,
} from "./buffers";

/**
 * Converts SharedArrayBuffer or any data to JSON object.
 * If data is SharedArrayBuffer, it decodes it considering possible compression.
 * Otherwise, it returns the data as is.
 *
 * @param data  The data to decode, which can be SharedArrayBuffer or any other type.
 * @param opts  Options for decoding, such as whether to decompress.
 * @returns The decoded JSON object or the original data if not a SharedArrayBuffer.
 */

export const decodeJSONSharedBuffer = <T>(
  data: SharedArrayBuffer | any,
  opts?: { decompress?: boolean; traceKey?: string }
): T | null => {
  if (data instanceof SharedArrayBuffer) {
    const uint8Array = bufferToUint8Array(data);
    const result = parseJSONUint8Array(uint8Array, opts);
    return result;
  }
  return data;
};
/**
 * Decodes an ArrayBufferLike into a JSON object, with optional decompression.
 *
 * @param arrayBuffer The ArrayBufferLike to decode.
 * @param opts Options for decoding, such as whether to decompress.
 * @returns The decoded JSON object.
 */

export const decodeJSONArrayBuffer = <T>(
  arrayBuffer: ArrayBufferLike,
  opts?: { useCompression?: boolean; traceKey?: string }
): T | null => {
  // console.time("decode-array-buffer-start");
  let uint8Array = bufferToUint8Array(arrayBuffer);
  const result = parseJSONUint8Array(uint8Array, opts);
  return result;
  // console.timeEnd("decode-array-buffer-start");
};
/**
 * Converts ArrayBufferLike or any data to JSON object.
 * If data is ArrayBufferLike, it decodes it considering possible compression.
 * Otherwise, it returns the data as is.
 *
 * @param data  The data to decode, which can be ArrayBufferLike, SharedArrayBuffer, or any other type.
 * @param opts Options for decoding, such as whether to decompress.
 * @returns The decoded JSON object or the original data if not an ArrayBufferLike.
 */

export function decodeJSONBuffer<T>(
  data: ArrayBufferLike | any,
  opts?: { useCompression?: boolean; traceKey?: string }
): T | null {
  if (data instanceof SharedArrayBuffer) {
    return decodeJSONSharedBuffer<T>(data, opts);
  } else if (data instanceof ArrayBuffer) {
    return decodeJSONArrayBuffer<T>(data, opts);
  } else {
    return data;
  }
}
/**
 *  Creates an ArrayBuffer from a JSON object, with optional compression.
 *
 * @param data  The JSON object to convert.
 * @param opts  Options for conversion, such as whether to compress.
 * @returns A new ArrayBuffer representing the JSON object.
 */

export const toJSONArrayBuffer = (
  data: any,
  opts?: { useCompression?: boolean; traceKey?: string }
) => {
  const arrayBuffer = convertJsonToUint8Array(data, opts).buffer;
  return arrayBuffer;
};
/**
 * Creates a SharedArrayBuffer from a JSON object, with optional compression.
 * If the input is already a SharedArrayBuffer, it is returned as is.
 * If the input is an ArrayBuffer, it is converted to SharedArrayBuffer.
 *
 * @param data The JSON object to convert.
 * @param opts Options for conversion, such as whether to compress.
 * @returns A SharedArrayBuffer representing the JSON object.
 */

export function toJSONSharedBuffer(
  data: ArrayBufferLike | any,
  opts?: { useCompression?: boolean; traceKey?: string }
): SharedArrayBuffer {
  if (data instanceof SharedArrayBuffer) {
    return data;
  } else if (data instanceof ArrayBuffer) {
    return createSharedBuffer(data);
  }

  let uint8Array = convertJsonToUint8Array(data, opts);
  const sharedBuffer = createSharedBuffer(uint8Array);
  return sharedBuffer;
}
